import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Skeleton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react"

import { getDnoCarousel } from "./carouselLogic/videoCarouselFaqDno"
import { getInstallerCarousel } from "./carouselLogic/videoCarouselFaqInstaller"
import VideoTile, { type IVideo } from "./videoTile"
import { useGetUserData } from "../../hooks/useGetUserData"
import Video from "../video/video"

interface IVideoCarouselProps {
    videos?: IVideo[]
    isLoading?: boolean
}

interface CarouselFlags {
    groups: string[]
    isSignedIn: boolean
    isInstallerUnapproved: boolean
    isDnoApproved: boolean
}

const getVisibleTiles = (
    isXs: boolean,
    isSm: boolean,
    isMd: boolean
): number => {
    if (isXs) return 1
    if (isSm) return 2
    if (isMd) return 3
    return 4
}

const VideoCarousel = ({
    videos = [],
    isLoading = false,
}: IVideoCarouselProps): ReactElement | null => {
    const {
        groups,
        isSignedIn,
        isInstallerUnapproved,
        isDnoApproved,
        isConfiguring,
    } = useGetUserData()
    const theme = useTheme()
    const isXs = useMediaQuery(theme.breakpoints.down("sm"))
    const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"))
    const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"))

    const visibleTiles = useMemo(
        () => getVisibleTiles(isXs, isSm, isMd),
        [isXs, isSm, isMd]
    )

    const userFlags: CarouselFlags = useMemo(
        () => ({
            groups,
            isSignedIn,
            isInstallerUnapproved,
            isDnoApproved,
        }),
        [groups, isSignedIn, isInstallerUnapproved, isDnoApproved]
    )

    const { videos: installerVideos, shouldShow: showInstaller } =
        getInstallerCarousel(userFlags)
    const { videos: dnoVideos, shouldShow: showDno } = getDnoCarousel(userFlags)

    const items = useMemo(() => {
        let defaultVideos: IVideo[] = []

        if (showInstaller) {
            defaultVideos = installerVideos
        } else if (showDno) {
            defaultVideos = dnoVideos
        }

        return videos.length > 0 ? videos : defaultVideos
    }, [videos, showInstaller, showDno, installerVideos, dnoVideos])

    const isLoadingState = isLoading || isConfiguring
    const shouldShowCarousel = items.length > 0 || isLoadingState

    const skeletonItems = useMemo(
        () =>
            Array.from(
                { length: Math.max(visibleTiles, 3) },
                (_, index) => index
            ),
        [visibleTiles]
    )

    const carouselRef = useRef<HTMLDivElement | null>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [selectedVideo, setSelectedVideo] = useState<IVideo | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)

    const updateScrollState = useCallback(() => {
        const carousel = carouselRef.current
        if (!carousel) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }

        const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth
        setCanScrollLeft(carousel.scrollLeft > 0)
        setCanScrollRight(carousel.scrollLeft < Math.max(0, maxScrollLeft - 1))
    }, [])

    useEffect(() => {
        updateScrollState()
    }, [items, visibleTiles, updateScrollState])

    const scrollCarousel = (direction: "left" | "right"): void => {
        const carousel = carouselRef.current
        if (!carousel) return

        const scrollAmount = carousel.clientWidth / visibleTiles
        carousel.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth",
        })
    }

    const closeDialog = useCallback(() => {
        setDialogOpen(false)
        setSelectedVideo(null)
    }, [])

    if (!shouldShowCarousel) {
        return null
    }

    return (
        <Stack width="100%" spacing={2}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 3, pt: 2, pb: 0 }}
            >
                <Typography
                    variant="h5"
                    paddingBottom={0}
                    fontWeight={600}
                    color={theme.palette.text.secondary}
                >
                    Help Videos
                </Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton
                        size="small"
                        onClick={() => scrollCarousel("left")}
                        disabled={isLoadingState || !canScrollLeft}
                        aria-label="Previous videos"
                    >
                        <KeyboardArrowLeftIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => scrollCarousel("right")}
                        disabled={isLoadingState || !canScrollRight}
                        aria-label="Next videos"
                    >
                        <KeyboardArrowRightIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <Box
                ref={carouselRef}
                onScroll={updateScrollState}
                sx={{
                    display: "flex",
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": {
                        display: "none",
                    },
                }}
            >
                {isLoadingState
                    ? skeletonItems.map((index) => (
                          <Box
                              key={`skeleton-${index}`}
                              sx={{
                                  minWidth: `${100 / visibleTiles}%`,
                                  flex: `0 0 ${100 / visibleTiles}%`,
                                  boxSizing: "border-box",
                                  p: 1,
                              }}
                          >
                              <Box
                                  sx={{
                                      width: "100%",
                                      aspectRatio: "16 / 9",
                                      borderRadius: 2,
                                      overflow: "hidden",
                                  }}
                              >
                                  <Skeleton
                                      variant="rounded"
                                      sx={{
                                          width: "100%",
                                          height: "100%",
                                      }}
                                  />
                              </Box>
                          </Box>
                      ))
                    : items.map((video) => (
                          <VideoTile
                              key={video.id}
                              video={video}
                              visibleTiles={visibleTiles}
                              onClick={(selected) => {
                                  setSelectedVideo(selected)
                                  setDialogOpen(true)
                              }}
                          />
                      ))}
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                keepMounted={false}
                fullWidth
                maxWidth="xl"
                PaperProps={{
                    sx: {
                        mx: 1,
                        width: "100%",
                        maxWidth: 1180,
                        maxHeight: "calc(100vh - 64px)",
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        pt: 2,
                    }}
                >
                    <Typography variant="h6">{selectedVideo?.title}</Typography>
                    <IconButton
                        aria-label="Close video dialog"
                        onClick={closeDialog}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <DialogContent
                    sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    {selectedVideo && (
                        <Box
                            sx={{
                                position: "relative",
                                width: "100%",
                                maxWidth: "100%",
                                aspectRatio: "16 / 9",
                                borderRadius: 2,
                                overflow: "hidden",
                                bgcolor: "common.black",
                                boxSizing: "border-box",
                                "& > div": {
                                    width: "100%",
                                    height: "100%",
                                    paddingTop: 0,
                                    margin: 0,
                                    boxSizing: "border-box",
                                },
                                "& iframe": {
                                    width: "100%",
                                    height: "100%",
                                    display: "block",
                                    border: 0,
                                },
                            }}
                        >
                            <Video videoUrl={selectedVideo.id} />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Stack>
    )
}

export default VideoCarousel
